import { Injectable } from '@angular/core';
import { TransformService } from '../transform/transform-service';
import { Borders, Fill, Workbook } from 'exceljs';
import { Coluna, ExcelFormats } from '../../models/mat-table-model';
import fs from 'file-saver';
import { LoaderService } from 'src/app/services/loader.service';
import { ReportService } from '../report/report-service';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ExcelService {

  border: Borders = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
    diagonal: { style: undefined }
  };

  fillHeaderFooter: Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: '9CC2E5' } //#9CC2E5
  }

  fillCell: Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: '1F4E78' } //#1F4E78
  }

  fillNegativeCell: Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'ffc7ce' } //#ffc7ce
  }

  fontHeaderFooter = {
    name: 'Calibri',
    size: 11,
    bold: true,
    color: { argb: '222A35' } //#222A35
  };

  fontCell = {
    name: 'Calibri',
    size: 10,
    bold: false,
    color: { argb: 'FFFFFF' } //#FFFFFF
  }

  constructor(
    public transformService: TransformService,
    private loaderService: LoaderService,
    private reportService: ReportService,
    private router: Router,
    private authService: AuthService) { }

  public async gerarPlanilha(listaDados, campos, header, titulo, nomeArquivo, configuracao: Coluna[] = []) {
    this.loaderService.setLoading(true);
    const lista = this.formatInfo(listaDados.map(a => ({ ...a })),)
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(titulo);

    const reportInfo = await this.configuraEstilo();

    await this.gerarCabecalho(workbook, worksheet, titulo, header.length, reportInfo);

    worksheet.addRow(header);

    const headerRow = worksheet.getRow(4);
    headerRow.font = this.fontHeaderFooter;
    headerRow.fill = this.fillHeaderFooter;
    headerRow.border = this.border;

    const rows = lista.map(registro =>
      campos.map((campo, colIndex) => {
        const valor = registro[campo] ?? '';
        const config = configuracao[colIndex];

        return this.formatValor(config, valor);
      })
    );

    const footer = campos.map((_, i) => configuracao[i]?.footer ?? '');

    worksheet.columns = configuracao.map(config => ({
      style: this.buscarEstilo(config)
    }));

    worksheet.addRows([...rows, footer]);

    const footerRow = worksheet.lastRow;
    footerRow?.eachCell(cell => {
      cell.font = this.fontHeaderFooter;
      cell.fill = this.fillHeaderFooter;
      cell.border = this.border;
    });

    this.autoWidth(worksheet);

    this.adicionaImagemCabecalho(worksheet, reportInfo);
    await this.gerarRodape(workbook, worksheet, titulo, lista.length + 4, header.length, reportInfo);

    worksheet.columns = worksheet.columns.map(config => ({
      width: config.width,
      style: {}
    }));


    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      fs.saveAs(blob, nomeArquivo);
      this.loaderService.setLoading(false);
    });
  }

  formatValor(config, valor) {
    switch (config?.excelFormat) {
      case ExcelFormats.MinuteToHour:
        return this.transformService.convertMinutesToTimeString(valor.toString());

      case ExcelFormats.Currency: {
        let v = parseFloat(valor) ?? 0;
        if (isNaN(v)) v = 0;
        return v;
      }

      case ExcelFormats.Function:
        return config.function(valor);

      case ExcelFormats.SimNao:
        return valor ? 'Sim' : 'Não';

      default:
        return valor;
    }
  }

  buscarEstilo(config: Coluna) {
    const baseStyle = {
      border: this.border,
      font: this.fontCell,
      fill: this.fillCell
    };

    switch (config?.excelFormat) {
      case ExcelFormats.Currency:
        return {
          ...baseStyle,
          numFmt: 'R$ #,##0.00;[Red]-R$ #,##0.00'
        };

      case ExcelFormats.MinuteToHour:
        return {
          ...baseStyle,
          numFmt: '[h]:mm'
        };

      default:
        return baseStyle;
    }

  }

  redimensionImage(curW: number, curH: number, maxW: number, maxH: number) {
    if (!isFinite(curW) || !isFinite(curH) || curW <= 0 || curH <= 0) {
      return { width: 0, height: 0 };
    }

    const fitsAlready = curW <= maxW && curH <= maxH;
    if (fitsAlready) {
      return { width: curW, height: curH };
    }

    curW = curW * 0.9;
    curH = curH * 0.9;

    return this.redimensionImage(curW, curH, maxW, maxH);

  }

  async adicionaImagemCabecalho(worksheet, reportInfo) {

    worksheet.getRow(1).height = 25;
    worksheet.getRow(2).height = 25;
    worksheet.getRow(3).height = 25;

    const { width: imgW, height: imgH } = await this.getImageSize(reportInfo.logoHeader);

    const maxWidth = ((worksheet.getColumn(1).width + worksheet.getColumn(2).width + worksheet.getColumn(3).width) * 7);

    const { width: newWidth, height: newHeight } = this.redimensionImage(imgW, imgH, maxWidth, 90);

    worksheet.addImage(0, { tl: { col: 0, row: 0 }, ext: { width: newWidth, height: newHeight } });

  }


  formatInfo(lista) {
    const yyyymmddRegex = /^\d{4}-\d{2}-\d{2}$/;
    const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
    const ISORegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z)?$/;

    const formatted = lista.map(el => {
      for (const [key, value] of Object.entries(el)) {
        if (typeof value === 'string') {
          if (yyyymmddRegex.test(value)) {
            el[key] = this.transformService.convertDateTimeToString(value, false);
          }
          else if (ISORegex.test(value) || dateTimeRegex.test(value)) {
            el[key] = this.transformService.convertDateTimeToString(value, true);
          }
        }
      }
      return el;
    });

    return formatted;
  }

  autoWidth = (worksheet, minimalWidth = 10) => {
    worksheet.columns.forEach((column) => {
      let maxColumnLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (cell.row >= 4) {
          maxColumnLength = Math.max(
            maxColumnLength,
            minimalWidth,
            cell.value ? cell.value.toString().length : 0
          );
        }

      });
      column.width = maxColumnLength + 2;
    });
  };

  formatCell(cell, config, isHeader = false, isFooter = false) {
    cell.border = this.border;
    if (isHeader || isFooter) {
      cell.font = this.fontHeaderFooter;
      cell.fill = this.fillHeaderFooter;
    } else {
      cell.font = this.fontCell;
      cell.fill = this.fillCell;
    }

    if (isHeader || (isFooter && !config.footer)) return;

    switch (config?.excelFormat) {
      case ExcelFormats.MinuteToHour:
        cell.value = this.transformService.convertMinutesToTimeString(cell.value.toString());
        break;

      case ExcelFormats.Currency:
        cell.numFmt = 'R$ #,##0.00;[Red]-R$ #,##0.00';
        cell.value = parseFloat(cell.value) ?? 0;
        if (isNaN(cell.value)) {
          cell.value = 0;
        }
        if (cell.value < 0) {
          cell.fill = this.fillNegativeCell;
        }
        break;

      case ExcelFormats.Function:
        cell.value = config.function(cell.value);
        break;

      case ExcelFormats.SimNao:
        cell.value = cell.value ? 'Sim' : 'Não';
        break;
    }
  }

  async configuraEstilo(): Promise<any> {
    try {
      const response = await firstValueFrom(this.reportService.getReportInfo());
      const parametros = response.body.retorno.ParametrosRelatorio;

      if (parametros) {

        parametros.forEach(element => {

          switch (element.rgn_codigo) {
            case "CorFundoCabecalho":
              this.fillHeaderFooter = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: element.rgn_valor.substring(1) }
              }
              break;
            case "CorFonteCabecalho":
              this.fontHeaderFooter = {
                name: 'Calibri',
                size: 11,
                bold: true,
                color: { argb: element.rgn_valor.substring(1) }
              };
              break;
            case "CorFundoCelula":
              this.fillCell = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: element.rgn_valor.substring(1) }
              }
              break;
            case "CorFonteCelula":
              this.fontCell = {
                name: 'Calibri',
                size: 11,
                bold: true,
                color: { argb: element.rgn_valor.substring(1) }
              };
              break;
          }
        });

        return response.body.retorno.reportInfo[0];
      }
    } catch (err) {
      this.loaderService.setLoading(false);
      return null;
    }
  }

  async gerarCabecalho(workbook, worksheet, titulo, qntColuna, reportInfo) {

    if (reportInfo && reportInfo.logoHeader) {
      workbook.addImage({
        base64: reportInfo.logoHeader,
        extension: reportInfo.logoHeaderExt,
      });

      worksheet.addRow([]);
      worksheet.addRow([]);
      worksheet.addRow([]);
      worksheet.addRow([]);

      worksheet.mergeCells(1, 1, 3, 3);
      const cellImagem = worksheet.getCell('A1');
      cellImagem.border = this.border;

      worksheet.mergeCells(1, 4, 3, 30);
      const cell = worksheet.getCell('D1');

      cell.border = this.border;

      let texto = "";

      if (reportInfo.nomeFilial && reportInfo.nomeLocal) {
        texto += reportInfo.nomeFilial + " - " + reportInfo.nomeLocal + "\n";
      }

      texto += "Planilha de " + titulo + "\n";
      texto += "Módulo: " + this.router.url.split('/')[1].toUpperCase() + " - " + reportInfo.nomeModulo;

      cell.value = texto;
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
      cell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF000000' } };

      worksheet.spliceRows(4, 1);
    }
  }

  async gerarRodape(workbook, worksheet, titulo, qntLinha, qntColuna, reportInfo) {


    if (reportInfo && reportInfo.logoFooter) {
      const imageId = workbook.addImage({
        base64: reportInfo.logoFooter,
        extension: reportInfo.logoFooterExt,
      });

      worksheet.mergeCells(qntLinha + 2, 1, qntLinha + 3, 1);

      const cellImagem = worksheet.getCell('A' + (qntLinha + 2));
      cellImagem.border = this.border;

      worksheet.mergeCells(qntLinha + 2, 2, qntLinha + 3, 30);
      const cell = worksheet.getCell('C' + (qntLinha + 2));

      cell.border = this.border;

      let texto = "";
      const data = new Date();

      texto += "Gerado por " + this.authService.authData.nomeUsuario + " - ";
      texto += `Em: ${data.toLocaleDateString('pt-BR')} às: ${data.toLocaleTimeString('pt-BR')}`;

      cell.value = texto;
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
      cell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FF000000' } };
      worksheet.getRow(cell.row).height = 20;


      const { width: imgW, height: imgH } = await this.getImageSize(reportInfo.logoFooter); 
      const maxWidth = (worksheet.getColumn(1).width * 7);
      const maxHeight = 25;
      
      console.log(imgW, imgH, maxWidth, maxHeight);
      const { width: newWidth, height: newHeight } = this.redimensionImage(imgW, imgH, maxWidth, maxHeight);

      worksheet.addImage(imageId, {
        tl: { col: 0, row: qntLinha + 1.4 },
        ext: { width: newWidth, height: newHeight }
      });

    }

  }

  getImageSize(base64: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const maxWidth = 250;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          const scale = maxWidth / width;
          width = maxWidth;
          height = height * scale;
        }

        resolve({ width, height });
      };
      img.src = `data:image/png;base64,${base64}`;
    });
  }
}